import { Calendar, Clock, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckBox from 'src/components/CheckBox';
import DateSelect from 'src/components/DateSelect/DateSelect';
import DeleteModal from 'src/components/DeleteModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import { DATE_FORMAT } from 'src/constants/common';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { useTimeZone } from 'src/hooks/useTimeZone';
import { setShipperOptions, setShippers } from 'src/redux/address.slice';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { setCreateOrderData } from 'src/redux/CreateOrders.slice';
import { isViewQuoteData } from 'src/redux/Quoting.slice';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import { logError } from 'src/services/LoggingService';
import { createDateWithoutTimezoneConversion, formatTimeInput, getDateWithSuffixFormat, getFormattedDateIgnoringTime, isShipperConsigneeAddressValid } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { PICKUP_DATE_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from '../AddQuote/Steps/addQuoteSteps.const';
import { PICKUP_WINDOW_TOOLTIP } from '../CreateQuote/Steps/Steps.const';
import { IAction, ILimitedAccessDeliveryData } from '../QuoteDetail/quote.interface';
import { consigneeFormKeys, initAction, shipperFormKeys } from '../quoteDetail.const';

import AdditionalServiceModal from './AdditionalServiceModal';
import EditShipperConsigneeForm from './EditShipperConsigneeForm';
import LimitedAccessDeliveryModal from './LimitedAccessDeliveryModal';

const ShipperDetails = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isViewQuote = useSelector(isViewQuoteData);
    const appearance = useSelector(appearanceSelector);
    const { isDateInPast } = useTimeZone();

    const { isServiceLoading = false, serviceTypes = { PICKUP: [], DELIVERY: [] }, shipperOptions = [], shippers = [], handleSearchShipper } = useAddressDetails();

    const [action, setAction] = useState<IAction>(initAction);
    const [isEditId, setIsEditId] = useState(false);
    const [isOpenFromAddNewShipper, setIsOpenFromAddNewShipper] = useState<boolean>(false);
    const [preEditId, setPreEditId] = useState<number | null>(null);
    const [deleteAction, setDeleteAction] = useState({
        mode: '',
        shipper: true,
        consignee: false,
        isContact: false,
        contactId: null as number | null,
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const savedPickupEndDateRef = useRef<string | null>(null);

    const {
        control,
        setValue,
        watch,
        getValues,
        clearErrors,
        trigger,
        formState: { errors, isSubmitted },
    } = useFormContext();

    const selectedServices = watch('serviceId');
    const pickupSensitivity = watch('pickupSensitivity');
    const pickupDate = watch('pickupDate');
    const pickupWindowType = watch('pickupWindowType');

    const watchCompanyName = watch('companyName');
    const watchShipperAddress = watch('shipperAddress');
    const watchShipperFullAddress = watch('shipperAddress')?.fullAddress;
    const watchId = watch('id');
    const watchShipperId = watch('shipperId');

    const isPickupDateInPast = isDateInPast(pickupDate);

    const selectedShipperBusinessHours = useMemo(() => {
        const entity = shippers?.find((s: any) => s.id === (watchId || watchShipperId));
        const start = entity?.businessHoursStartTime ? String(entity.businessHoursStartTime).slice(0, 5) : null;
        const end = entity?.businessHoursEndTime ? String(entity.businessHoursEndTime).slice(0, 5) : null;

        return start && end ? `${start} – ${end}` : null;
    }, [watchId, watchShipperId, shippers]);

    const handleAddress = (key: string, value: any) => {
        const mapping = key === 'shipper' ? shipperFormKeys : consigneeFormKeys;
        Object.entries(mapping).forEach(([formKey, dataKey]) => {
            setValue(formKey, value[dataKey]);
        });
    };

    const handleAction =
        (type: string, isOpen: boolean = true, mode: any = '') =>
        () => {
            setAction((old) => ({ ...old, [type]: isOpen, mode }));
        };

    const handleServiceChange = (e: any) => {
        let { value, checked } = e.target;
        value = parseInt(value);

        const currentServices = Array.isArray(selectedServices) ? selectedServices : [];

        if (checked) {
            if (e.target.name === 'limited_access_delivery') {
                handleAction('limitedAccess')();
            } else {
                setValue('serviceId', [...currentServices, value]);
            }
        } else {
            const filteredServices = currentServices.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (e.target.name === 'limited_access_delivery') {
                setValue('limitedAccess', false);
                setValue('limitedAccessOther', '');
                setValue('limitedAccessType', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'limited_access_delivery') {
            handleAction('limitedAccess')();
        }
    };

    const getList = (data: any) => (
        <div className="flex flex-wrap -m-1">
            {data.map((val: any) => (
                <div className="flex p-1 w-1/3" key={`service_${val.id}`}>
                    <CheckBox
                        id={`service_${val.id}`}
                        onChangeFunc={handleServiceChange}
                        name={val.slug}
                        value={val.id}
                        checked={selectedServices?.includes(val.id) || false}
                        parentClassName="mt-px"
                        disabled={val.isOnlyForFTL || isViewQuote}
                    />
                    <div className="leading-[0.9] space-x-1.5 flex items-center truncate pl-1.5 flex-1">
                        {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                            <TooltipCmp message={val.name} childrenClassName="!flex">
                                <label
                                    htmlFor={`service_${val.id}`}
                                    className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : ''} ${
                                        val.isOnlyForFTL ? 'line-through' : 'cursor-pointer'
                                    }`}
                                >
                                    {val.slug === 'pickup_appointment_required'
                                        ? t('QuotingHub.Common.Pickup_Appointment')
                                        : val.slug === 'delivery_appointment_required'
                                        ? t('QuotingHub.Common.Delivery_Appointment')
                                        : t('QuotingHub.Common.After_Hours_Pickup')}
                                </label>
                            </TooltipCmp>
                        ) : (
                            <label
                                htmlFor={`service_${val.id}`}
                                className={`leading-4 text-textSecondary text-xs font-medium truncate ${isViewQuote ? '!cursor-not-allowed' : ''} ${
                                    val.isOnlyForFTL ? 'line-through' : 'cursor-pointer'
                                }`}
                            >
                                {val.slug === 'after_hours_pickup' ? t('QuotingHub.Common.After_Hours_Pickup') : val.name}
                            </label>
                        )}
                        {selectedServices?.includes(val.id) && val.slug === 'limited_access_delivery' ? (
                            <TooltipCmp message={t('Common.Edit')}>
                                <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                            </TooltipCmp>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('limitedAccess', true);
        setValue('limitedAccessOther', data.limitedAccessOther);
        setValue('limitedAccessType', data.selectedLimitedAccessType);
        const limitedAccessId = serviceTypes.DELIVERY.find((val: any) => val.slug === 'limited_access_delivery')?.id;
        const currentServiceId = getValues('serviceId') || [];
        const updatedServiceId = currentServiceId.includes(limitedAccessId) ? currentServiceId : [...currentServiceId, limitedAccessId];
        setValue('serviceId', updatedServiceId);
    };

    const resetShipperForm = () => {
        setValue('companyName', '');
        setValue('shipperAddress', null);
        setValue('shipperFullAddress', '');
        setValue('shipperAddress1', null);
        setValue('shipperStreetNumber', null);
        setValue('shipperCity', null);
        setValue('shipperState', null);
        setValue('shipperCountry', null);
        setValue('shipperPostal', null);
        setValue('shipperLatitude', null);
        setValue('shipperLongitude', null);
        setValue('shipperStateCode', null);
        setValue('shipperCountryCode', null);
        setValue('shipperCompanyName', null);
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        setValue('id', null);
        setValue('shipperId', null);
        setValue('companyName', '');
        setValue('shipperAddress', null);
        clearErrors('addressMismatch' as any);
        setIsOpenFromAddNewShipper(true);
    };

    const handleSwitchShipper = () => {
        setValue('id', null);
        setValue('shipperId', null);
        resetShipperForm();
        clearErrors('addressMismatch' as any);
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            const currentId = getValues('id') || getValues('shipperId');
            setPreEditId(currentId ? +currentId : null);
            setIsEditId(true);
        } else if (value === 'delete_id') {
            setDeleteAction({ mode: 'delete', shipper: true, consignee: false, isContact: false, contactId: null });
        } else if (value === 'switch_id') {
            handleSwitchShipper();
        }
    };

    const handleEditShipper = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);

        if (!isEdit) {
            const isValidId = preEditId && (preEditId === watchId ? watchId : watchShipperId);

            if (isValidId) {
                setPreEditId(null);
            } else {
                setValue('id', null);
                setValue('shipperId', null);
                resetShipperForm();
            }
        }
    };

    const handleSaveShipper = (data: any) => {
        setIsEditId(false);
        setValue('id', data.id);
        setValue('shipperId', data.id);
        setValue('companyName', data.companyName);
        setValue('shipperAddress', data.address || data);

        if (data.address || data) {
            handleAddress('shipper', data.address || data);
        }
        setPreEditId(null);
        clearErrors('addressMismatch' as any);
    };

    const handleGoogleAddressSelect = (addressValue: any) => {
        const addressObject = {
            fullAddress: addressValue?.fullAddress ?? '',
            address1: addressValue?.address1 ?? '',
            city: addressValue?.city ?? '',
            country: addressValue?.country ?? '',
            countryCode: addressValue?.countryCode ?? '',
            latitude: addressValue?.latitude ?? '',
            longitude: addressValue?.longitude ?? '',
            postal: addressValue?.postal ?? '',
            state: addressValue?.state ?? '',
            stateCode: addressValue?.stateCode ?? '',
            streetNumber: addressValue?.streetNumber ?? '',
            timezone: addressValue?.timezone ?? '',
            additionalServiceIds: addressValue?.additionalServiceIds ?? [],
            companyName: '',
        };
        setValue('id', null);
        setValue('shipperId', null);
        setValue('companyName', '');
        setValue('shipperAddress', addressObject);
        handleAddress('shipper', addressObject);
        clearErrors('addressMismatch' as any);
        // Autocomplete addresses are never saved — always open form so the user
        // can review and fill in any missing fields (e.g. street number, postal).
        setIsEditId(true);
    };

    const handleShipperChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            setValue('shipperId', value.value);
            const data = shippers.find((shipperData: any) => shipperData.id === value.value);

            if (data) {
                setValue('companyName', data.companyName);
                setValue('shipperAddress', data);
                handleAddress('shipper', data);
                clearErrors('addressMismatch' as any);

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditId(true);
                }
            }
        } else {
            onChange(null);
            setValue('shipperId', null);
            resetShipperForm();
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        setDeleteAction({ mode: '', shipper: false, consignee: false, isContact: false, contactId: null });

        if (closeStatus) {
            setIsDeleting(true);
            const idToDelete = watchId ? +watchId : watchShipperId ? +watchShipperId : null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            setValue('id', null);
                            setValue('shipperId', null);
                            setValue('companyName', '');
                            setValue('shipperAddress', '');
                            setValue('shipperFullAddress', '');
                            setValue('shipperAddress1', null);
                            setValue('shipperStreetNumber', null);
                            setValue('shipperCity', null);
                            setValue('shipperState', null);
                            setValue('shipperCountry', null);
                            setValue('shipperPostal', null);
                            setValue('shipperLatitude', null);
                            setValue('shipperLongitude', null);
                            setValue('shipperStateCode', null);
                            setValue('shipperCountryCode', null);
                            setValue('shipperCompanyName', null);

                            const updatedShippers = shippers.filter((shipper: any) => shipper.id !== idToDelete);
                            const updatedShipperOptions = shipperOptions.filter((option: any) => option.value !== idToDelete);
                            dispatch(setShippers(updatedShippers));
                            dispatch(setShipperOptions(updatedShipperOptions));
                            dispatch(setCreateOrderData({ data: { shipperDetails: null } }));

                            setIsEditId(false);
                            WalToast.success(t('ValidationMessages.Shipper_removed'));
                        }
                    })
                    .catch(async (error: any) => {
                        const errorMessage = error?.response?.data?.message || t('ValidationMessages.Shipper_delete_failed');
                        WalToast.error(errorMessage);

                        await logError(error, {
                            errorMessage: error?.message || 'Error while deleting shipper',
                            errorLocation: 'ShipperDetails: handleModalClose',
                            storeInDatabase: true,
                            logToConsole: true,
                        });
                    })
                    .finally(() => {
                        setIsDeleting(false);
                    });
            } else {
                setIsDeleting(false);
            }
        }
    };

    useEffect(() => {
        if (watchId) {
            const currentLatitude = getValues('shipperLatitude');

            if (watchShipperAddress && currentLatitude) {
                return;
            }

            const data = shippers.find((shipperData: any) => shipperData.id === watchId);

            if (data) {
                setValue('companyName', data.companyName);
                setValue('shipperAddress', data);
                handleAddress('shipper', data);
                clearErrors('addressMismatch' as any);
            }
        } else {
            const currentLatitude = getValues('shipperLatitude');

            if (!currentLatitude && !watchShipperAddress) {
                resetShipperForm();
            }
        }
    }, [shippers, watchId]);

    useEffect(() => {
        if (watchShipperId) {
            const currentLatitude = getValues('shipperLatitude');

            if (watchShipperAddress && currentLatitude) {
                return;
            }

            const data = shippers.find((shipperData: any) => shipperData.id === watchShipperId);

            if (data) {
                setValue('companyName', data.companyName);
                setValue('shipperAddress', data);
                handleAddress('shipper', data);
            }
        }
    }, [watchShipperId, shippers]);

    // Open edit form on mount if the pre-loaded address is unsaved or missing required fields.
    useEffect(() => {
        if (watchShipperAddress?.fullAddress) {
            const isValid = isShipperConsigneeAddressValid(watchShipperAddress);
            const hasId = !!(watchId || watchShipperId);

            if (!isValid || !hasId) {
                setIsEditId(true);
            }
        }
    }, []);

    // Pickup field handlers
    const getMaximumPickupDate = () => {
        const deliveryDate = watch('deliveryDate');

        if (!isPickupDateInPast && deliveryDate && moment(deliveryDate).isValid()) {
            return deliveryDate;
        }

        return undefined;
    };

    const getMinimumDate = useCallback(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone)), [appearance?.timezone]);

    const handlePickupDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);

        const currentPickupEndDate = watch('pickupEndDate');

        if (currentPickupEndDate && moment.utc(value).isAfter(moment.utc(currentPickupEndDate), 'day')) {
            setValue('pickupEndDate', null);

            if (isSubmitted) {
                trigger('pickupEndDate');
            }
        }

        const deliveryDate = watch('deliveryDate');

        if (deliveryDate && moment.utc(deliveryDate).isBefore(moment.utc(value), 'day')) {
            setValue('deliveryDate', null);

            if (isSubmitted) {
                trigger('deliveryDate');
            }
        }
    };

    const handlePickupSensitivityChange = (val: string, onChange: any) => () => {
        const currentEndDate = getValues('pickupEndDate');
        onChange(val);

        if (val === 'fixed') {
            savedPickupEndDateRef.current = currentEndDate ?? null;
            setValue('pickupEndDate', null);

            if (isSubmitted) {
                trigger('pickupEndDate');
            }
        } else if (val === 'flexible') {
            const restored = savedPickupEndDateRef.current;

            if (restored) {
                setValue('pickupEndDate', restored);
                savedPickupEndDateRef.current = null;
            }
        }
    };

    const handlePickupWindowTypeChange = (val: string, onChange: any) => () => {
        onChange(val);

        if (val === 'open') {
            setValue('pickupStartTime', null);
            setValue('pickupEndTime', null);
        } else if (val === 'appointment') {
            setValue('pickupEndTime', null);
        }

        clearErrors(['pickupStartTime', 'pickupEndTime']);

        if (isSubmitted) {
            trigger('pickupStartTime');
        }
    };

    const handleTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void, previousValue: string) => {
        const formattedTime = formatTimeInput(event.target.value, previousValue);
        onChange(formattedTime);
    };

    return (
        <>
            {/* Shipper Details */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">
                    {t('Common.Shipper_Details')}
                    <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                </h6>
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 flex flex-wrap gap-x-2 space-y-3">
                    <ShipperConsigneeAutocomplete
                        control={control}
                        isShipper={true}
                        isEditId={isEditId}
                        watchCompanyName={watchCompanyName}
                        watchShipperAddress={watchShipperAddress}
                        watchShipperFullAddress={watchShipperFullAddress}
                        shipperOptions={shipperOptions}
                        consigneeOptions={[]}
                        errors={errors}
                        handleShipperChange={handleShipperChange}
                        handleCreateNew={handleCreateNew}
                        handleCardActionType={handleCardActionType}
                        isAddressLoading={!!watchShipperAddress && !watchShipperFullAddress}
                        showDeleteOption={!!watchId || !!watchShipperId}
                        handleGoogleAddressSelect={handleGoogleAddressSelect}
                        watchShipperConsigneeId={(watchId || watchShipperId) && shippers.find((shipper) => shipper.id === (watchId || watchShipperId)) ? watchId || watchShipperId : null}
                        onInputChange={(e: any, { action: inputAction }: any) => {
                            if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
                                handleSearchShipper(e);
                            }
                        }}
                    />
                    {isEditId && (
                        <EditShipperConsigneeForm
                            handleEditShipper={handleEditShipper}
                            id={
                                isOpenFromAddNewShipper
                                    ? undefined
                                    : shippers.find((shipper) => Number(shipper.id) === Number(watchId || watchShipperId))
                                    ? watchId
                                        ? +watchId
                                        : +watchShipperId
                                    : undefined
                            }
                            handleSaveShipper={handleSaveShipper}
                            isShipper={true}
                            initialCompanyName={watchCompanyName}
                            initialAddress={watchShipperAddress}
                            key={shippers.length}
                        />
                    )}

                    {/* Pickup Additional Services */}
                    <div className="space-y-1.5 w-full">
                        <div className="flex items-center mb-1.5">
                            <h6 className="form_label block mb-0">{t('QuotingHub.Common.Pickup_Additional_Services')}</h6>
                            <div className={`cursor-pointer ${isServiceLoading ? 'custom-loading' : ''}`} onClick={handleAction('service')}>
                                <TooltipCmp message={t('QuotingHub.Common.Pickup_Additional_Services')}>
                                    <InfoCircle className="w-3.5 h-3.5 ml-1 text-primary cursor-pointer" />
                                </TooltipCmp>
                            </div>
                        </div>
                        <div className="rounded-lg border border-utilityGray200 bg-white shadow-sm p-3">
                            {!isServiceLoading && serviceTypes.PICKUP?.length > 0 ? (
                                getList(serviceTypes.PICKUP)
                            ) : isServiceLoading ? (
                                <div className="flex flex-wrap -m-1">
                                    {Array.from({ length: 12 }).map((_, index) => (
                                        <div className="flex p-1 w-1/3" key={`service_${index}`}>
                                            <CheckBox id={`service_abc`} onChangeFunc={handleServiceChange} name="abc" value="xyz" checked={false} parentClassName="mt-0.5 custom-loading" />
                                            <div className="flex leading-4">
                                                <label htmlFor={`service_abc`} className="md:ml-2 ml-1.5 text-sm leading-4 text-textSecondary font-medium xxl:me-2.5 me-1.5 custom-loading">
                                                    &nbsp;
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <NotFoundUI
                                    iconClassName="!hidden"
                                    title={t('QuotingHub.QuotingDashboard.No_Services_Found')}
                                    desc={t('QuotingHub.QuotingDashboard.No_Pickup_service_found')}
                                    containerClassName="!h-auto !my-5"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                {/* Pickup Sensitivity + Date */}
                <div className="flex gap-x-3 items-end">
                    <Controller
                        name="pickupSensitivity"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <SelectBox
                                label={t('QuotingHub.CreateQuote.Pickup_Sensitivity')}
                                labelClassName="form_label block mb-1.5"
                                required
                                parentClassName="w-full"
                                options={PICKUP_DATE_TYPE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                                value={value ? PICKUP_DATE_TYPE_OPTIONS.find((option) => value === option.value) : null}
                                onChangeFunc={(selectedOption: any) => {
                                    handlePickupSensitivityChange(selectedOption?.value, onChange)();
                                }}
                                className="w-full"
                                isError={!!error}
                                isDisabled={isViewQuote}
                                placeholder={t('QuotingHub.CreateQuote.Pickup_Sensitivity')}
                            />
                        )}
                    />

                    <Controller
                        name="pickupDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                labelClassName="form_label mb-0"
                                required
                                inputName="pickupDate"
                                className="form_control w-full"
                                placeholder={DATE_FORMAT}
                                dateFormat="dd/MM/yyyy"
                                minDate={getMinimumDate()}
                                maxDate={getMaximumPickupDate()}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                onChangeFunc={handlePickupDateChange(onChange)}
                                errorText={error?.message ? true : false}
                                icon={<Calendar className="h-5 w-5" />}
                                parentClassName="w-full one-month-datepicker"
                                dateSelectClassName={isViewQuote ? 'pointer-events-none' : ''}
                                ReactDatePickerSelectWrapClass={isViewQuote ? '!cursor-not-allowed' : ''}
                                disabled={isViewQuote}
                            />
                        )}
                    />
                    {/* Pickup End Date — flexible only */}
                    {pickupSensitivity === 'flexible' && (
                        <Controller
                            name="pickupEndDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    labelClassName="form_label block mb-1.5"
                                    inputName="pickupEndDate"
                                    required
                                    className="form_control w-full"
                                    placeholder={t('Common.End_date')}
                                    dateFormat="dd/MM/yyyy"
                                    minDate={pickupDate ? createDateWithoutTimezoneConversion(moment(pickupDate)) : getMinimumDate()}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={(event: any) => {
                                        onChange(event.value);
                                        clearErrors('pickupEndDate');
                                    }}
                                    errorText={!!error}
                                    icon={<Calendar className="h-5 w-5" />}
                                    parentClassName="w-full one-month-datepicker"
                                    dateSelectClassName={isViewQuote ? 'pointer-events-none' : ''}
                                    ReactDatePickerSelectWrapClass={isViewQuote ? '!cursor-not-allowed' : ''}
                                    disabled={isViewQuote}
                                />
                            )}
                        />
                    )}
                </div>
            </div>
            <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                <div className="flex flex-col w-full">
                    {/* Pickup Window */}
                    <div className="flex gap-x-3 items-end">
                        <Controller
                            name="pickupWindowType"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <div className="flex-1">
                                    <div className="flex items-center mb-1.5">
                                        <span className="form_label !mb-0">{t('BookOrder.Pickup_Window')}</span>
                                    </div>
                                    <SelectBox
                                        parentClassName="w-full"
                                        options={PICKUP_WINDOW_TYPE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                                        value={value ? PICKUP_WINDOW_TYPE_OPTIONS.find((option) => value === option.value) : null}
                                        onChangeFunc={(selectedOption: any) => {
                                            handlePickupWindowTypeChange(selectedOption?.value, onChange)();
                                        }}
                                        className="w-full"
                                        isError={!!error}
                                        isDisabled={isViewQuote}
                                        placeholder={t('QuotingHub.Common.Select_Pickup_Window')}
                                    />
                                </div>
                            )}
                        />

                        {/* Start Time */}
                        {pickupWindowType !== 'open' && (
                            <Controller
                                name="pickupStartTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={`flex-1 ${isViewQuote ? 'pointer-events-none' : ''}`}>
                                        <InputMaskCmp
                                            inputName="pickupStartTime"
                                            placeholder={t('Orders.Order_Stepper.Shipper_Details.HH_MM')}
                                            parentClassName="flex-1"
                                            value={(value as string) ?? ''}
                                            HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                            className={error?.message ? 'border border-red-500 border-solid' : ''}
                                            mask="99:99"
                                            icon={
                                                <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                    <Clock className="h-4 w-4" />
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            />
                        )}

                        {/* End Time — ready_time only */}
                        {pickupWindowType === 'ready_time' && (
                            <Controller
                                name="pickupEndTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={`flex-1 ${isViewQuote ? 'pointer-events-none' : ''}`}>
                                        <InputMaskCmp
                                            inputName="pickupEndTime"
                                            placeholder={`${t('Orders.Order_Stepper.Shipper_Details.HH_MM')} (optional)`}
                                            parentClassName="flex-1"
                                            value={(value as string) ?? ''}
                                            HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                            mask="99:99"
                                            isError={!!error}
                                            icon={
                                                <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                    <Clock className="h-4 w-4" />
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            />
                        )}
                    </div>

                    {PICKUP_WINDOW_TOOLTIP[pickupWindowType] && (
                        <span className="mt-1.5 text-[12px] font-normal leading-[18px] text-grayLight600 italic">
                            {pickupWindowType === WINDOW_TYPE.OPEN && selectedShipperBusinessHours
                                ? PICKUP_WINDOW_TOOLTIP[pickupWindowType].replace('business hours', `business hours (${selectedShipperBusinessHours})`)
                                : PICKUP_WINDOW_TOOLTIP[pickupWindowType]}
                        </span>
                    )}
                </div>
            </div>

            {/* Modals */}
            {action.service && <AdditionalServiceModal handleClose={handleAction('service', false)} services={serviceTypes?.PICKUP || []} title={t('QuotingHub.Common.Pickup_Additional_Services')} />}
            {action.limitedAccess && (
                <LimitedAccessDeliveryModal
                    handleClose={handleAction('limitedAccess', false)}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={{
                        limitedAccessType: watch('limitedAccessType') || '',
                        limitedAccessOther: watch('limitedAccessOther') || '',
                    }}
                />
            )}
            {deleteAction.mode === 'delete' && (
                <DeleteModal
                    handleClose={handleModalClose}
                    moduleTitle={t('Orders.remove_from_wal_hq', { companyName: watchCompanyName || '' })}
                    moduleName={t('Common.Shipper')}
                    isDeleteLoading={isDeleting}
                />
            )}
        </>
    );
};

export default ShipperDetails;
